import { calcularRemessaExteriorEstimativa, etapaEstimativaAtual, linhaElegivelRemessaExterior } from './remessaExteriorEstimativaPolicy';
import { cambioBrlLinha, fatorMoedaOrcamento, temCambioProprio } from './moedaFornecedorPolicy';
import { ChiesiAgenciaImpostoPolicy } from './chiesiAgenciaImpostoPolicy';
import { ImpostoNfAccessPorFornecedorPolicy } from './impostoNfAccessPorFornecedorPolicy';
import { CAMPO_SOMA_SEM_ARREDONDAR, moedaOrcamentoDeBrl, orcamentoBrlExato, orcamentoEstrangeiro, valorBrlLinha } from './valorBrlPolicy';
import { ehSomenteVisualizacao } from './somenteVisualizacaoPolicy';
import { ehImpostoNfContaDireta, fatorBaseNf } from './impostoNfContaDiretaPolicy';

// Coluna vigente (espelho de accessapi app/Services/OrcamentoColunaVigente.php — mesma regra,
// mesmos números). Regra do Rafael (13/09 teto do fornecedor; 25/09 valorTotal do orçamento):
// soma as linhas por etapa e vale "sempre a última coluna preenchida" no AGREGADO — Fechamento
// se a soma > 0, senão Negociado se > 0, senão Pré-Evento. Nunca por linha: a linha sem valor
// na coluna escolhida não tem custo nessa etapa (Fechamento em branco é gravado 0).
// Internacional (item 3): linha paga no exterior com ledger ABERTO não preenche o Fechamento e,
// com Fechamento vigente, entra com a estimativa (Negociado, senão Pré-Evento); ledger CONCLUÍDO
// entra com o principal pago (BRL real; ÷ câmbio na moeda do orçamento) e conta como preenchido.
export const ETAPAS_VIGENTE = ['Fechamento', 'Negociado', 'PreEvento'];
// valorTotal (A01, 28/09): a coluna é escolhida por FORNECEDOR; 'PorFornecedor' = fornecedores em colunas diferentes.
export const ETAPA_POR_FORNECEDOR = 'PorFornecedor';
export const ROTULO_ETAPA_VIGENTE = { Fechamento: 'Fechamento', Negociado: 'Negociado', PreEvento: 'Pré-Evento', PorFornecedor: 'por fornecedor' };

const numero = (valor) => {
    if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
    if (valor === null || valor === undefined) return 0;
    const texto = String(valor).trim();
    if (!texto) return 0;
    const convertido = Number(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto);
    return Number.isFinite(convertido) ? convertido : 0;
};
const centavos = (valor) => Math.round((valor + Number.EPSILON) * 100) / 100;

// Estado padrão na tela: o ledger concluído já vem projetado em `fechamento_exibicao` (API ou prévia).
export function estadoLinhaColunaVigente(linha = {}, fornecedorAccess = null) {
    if (linha?.fechamento_exibicao?.origem === 'ledger') return { estado: 'ledger', principal_brl: numero(linha.fechamento_exibicao.principal_brl) };
    if (linhaElegivelRemessaExterior(linha, fornecedorAccess)) return { estado: 'aberta', principal_brl: 0 };
    return { estado: 'normal', principal_brl: 0 };
}

// Moeda do fornecedor (27/09): linha com câmbio próprio converte pelo câmbio DELA na etapa (em BRL)
// ou pelo fator câmbio-da-linha ÷ câmbio global (moeda do orçamento). Legado: câmbio global, como sempre.
export function valorLinhaNaEtapa(linha = {}, etapa, estado = { estado: 'normal' }, cambio = 1, emBrl = false) {
    const fator = emBrl ? cambio : 1;
    const proprio = temCambioProprio(linha);
    const fatorMomento = (momento) => {
        if (!proprio) return fator;
        return emBrl ? cambioBrlLinha(linha, momento, cambio) : fatorMoedaOrcamento(linha, momento, cambio);
    };
    if (etapa === 'Fechamento' && estado.estado === 'ledger') return emBrl ? estado.principal_brl : estado.principal_brl / cambio;
    if (etapa === 'Fechamento' && estado.estado === 'aberta') {
        const estimativa = etapaEstimativaAtual(linha);
        if (!estimativa) return 0;
        const momento = estimativa === 'neg' ? 'Negociado' : 'PreEvento';
        return numero(linha[`total${momento}`]) * fatorMomento(momento);
    }
    return numero(linha[`total${etapa}`]) * fatorMomento(etapa);
}

export function escolherEtapaVigente(somas = {}) {
    if (centavos(somas.Fechamento || 0) > 0) return 'Fechamento';
    if (centavos(somas.Negociado || 0) > 0) return 'Negociado';
    return 'PreEvento';
}

// `valores` segue a ordem de `linhas`. `estadoLinha` permite à prévia do fechamento exterior
// informar o ledger calculado antes de existir `fechamento_exibicao`.
// etapaForcada: linhas sem fornecedor seguem a coluna do ORÇAMENTO (Rafael 28/09, decisão A).
export function resolverColunaVigente(linhas = [], { cambio = 1, emBrl = false, fornecedorAccess = null, estadoLinha, etapaForcada = null } = {}) {
    const fatorCambio = Number.isFinite(cambio) && cambio > 0 ? cambio : 1;
    const estados = linhas.map((linha) => (estadoLinha ? estadoLinha(linha) : estadoLinhaColunaVigente(linha, fornecedorAccess)));
    const somas = { Fechamento: 0, Negociado: 0, PreEvento: 0 };
    // Item só para visualização (D-0113): vale 0 e não "preenche" coluna — fora ANTES da escolha da etapa.
    const fora = linhas.map((linha) => ehSomenteVisualizacao(linha));
    linhas.forEach((linha, i) => ETAPAS_VIGENTE.forEach((etapa) => {
        if (fora[i]) return;
        // Rascunho de Fechamento de linha exterior aberta não "preenche" a coluna.
        if (etapa === 'Fechamento' && estados[i].estado === 'aberta') return;
        somas[etapa] += valorLinhaNaEtapa(linha, etapa, estados[i], fatorCambio, emBrl);
    }));
    const etapa = ETAPAS_VIGENTE.includes(etapaForcada) ? etapaForcada : escolherEtapaVigente(somas);
    const valores = linhas.map((linha, i) => (fora[i] ? 0 : valorLinhaNaEtapa(linha, etapa, estados[i], fatorCambio, emBrl)));
    return { etapa, total: valores.reduce((soma, valor) => soma + valor, 0), valores, somas };
}

// Chave do fornecedor (espelho de OrcamentoColunaVigente::chaveFornecedor = PainelFinanceiroValores::chave):
// fornecedor (fornecedorpagamento não canônico vence `fornecedor`) × quem paga ('cliente' separado de
// agência/não informado). Linha SEM fornecedor (FEE, 1945, legado) = '_sem_fornecedor': não decide coluna
// própria — segue a última coluna preenchida do ORÇAMENTO inteiro (Rafael 28/09, decisão A).
export const SEM_FORNECEDOR = '_sem_fornecedor';
export function chaveFornecedorColunaVigente(linha = {}) {
    // A tela guarda {id, nome} (ex.: { id: 'agencia' }); a API/banco, a string.
    const bruto = linha.fornecedorpagamento ?? null;
    const pagador = bruto && typeof bruto === 'object' ? (bruto.id ?? null) : bruto;
    const fornecedor = linha.fornecedor && typeof linha.fornecedor === 'object' ? linha.fornecedor.id : linha.fornecedor;
    let id = pagador && !['agencia', 'cliente'].includes(String(pagador)) ? String(pagador) : String(fornecedor ?? '');
    if (id === '' || id === 'undefined' || id === 'null') id = SEM_FORNECEDOR;
    return `${id}|${pagador === 'cliente' ? 'cliente' : 'agencia'}`;
}

// valorTotal do orçamento (A01, 28/09; espelho de OrcamentoColunaVigente::resolverPorFornecedor): a
// última coluna preenchida é escolhida no AGREGADO DE CADA FORNECEDOR, nunca uma só para o orçamento
// (o Fechamento de um fornecedor zerava o Negociado de outro que ainda não fechou). Fechamento 0/em
// branco do fornecedor não conta como preenchido. `valores`/`etapas` seguem a ordem de `linhas`;
// `etapa` = coluna comum aos fornecedores com valor, ou 'PorFornecedor' quando diferem.
export function resolverColunaVigentePorFornecedor(linhas = [], opcoes = {}) {
    const grupos = new Map();
    linhas.forEach((linha, i) => {
        const chave = chaveFornecedorColunaVigente(linha);
        if (!grupos.has(chave)) grupos.set(chave, []);
        grupos.get(chave).push(i);
    });
    const valores = linhas.map(() => 0);
    const etapas = linhas.map(() => 'PreEvento');
    const somas = { Fechamento: 0, Negociado: 0, PreEvento: 0 };
    const comValor = new Set();
    // Linhas sem fornecedor: "cada coluna representa apenas o seu valor" — valem na última coluna em que
    // QUALQUER linha do orçamento tem valor (Rafael 28/09, decisão A).
    const semFornecedor = (chave) => chave.startsWith(`${SEM_FORNECEDOR}|`);
    const etapaOrcamento = [...grupos.keys()].some(semFornecedor) ? resolverColunaVigente(linhas, opcoes).etapa : null;
    grupos.forEach((indices, chave) => {
        const r = resolverColunaVigente(indices.map((i) => linhas[i]), { ...opcoes, etapaForcada: semFornecedor(chave) ? etapaOrcamento : null });
        indices.forEach((i, k) => { valores[i] = r.valores[k]; etapas[i] = r.etapa; });
        ETAPAS_VIGENTE.forEach((etapa) => { somas[etapa] += r.somas[etapa]; });
        if (centavos(r.somas.Fechamento + r.somas.Negociado + r.somas.PreEvento) > 0) comValor.add(r.etapa);
    });
    const etapa = comValor.size > 1 ? ETAPA_POR_FORNECEDOR : ([...comValor][0] || 'PreEvento');
    return { etapa, total: valores.reduce((soma, valor) => soma + valor, 0), valores, etapas, somas };
}

// Etapa da estimativa de remessa ('neg'/'pre') que compõe o Total Atual da linha exterior aberta.
export function etapaEstimativaLinha(linha = {}, etapaVigente = 'Fechamento') {
    if (etapaVigente === 'Fechamento') return etapaEstimativaAtual(linha);
    if (numero(linha[`total${etapaVigente}`]) <= 0) return null;
    return etapaVigente === 'Negociado' ? 'neg' : 'pre';
}

const cambioOrcamento = (orcamento = {}) => {
    if (String(orcamento.moeda || 'real').toLowerCase() === 'real') return 1;
    const cambio = numero(orcamento.cambio);
    return cambio > 0 ? cambio : 1;
};

const semAcento = (valor) => String(valor || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
const flagAtiva = (valor) => {
    if (typeof valor === 'boolean') return valor;
    if (typeof valor === 'number') return valor === 1;
    return ['1', 'sim', 'true', 'yes', 'on'].includes(String(valor ?? '').trim().toLowerCase());
};
// Imposto NF Access do grupo (espelho de OrcamentoImpostoNfAccess::calcular): base × 19,05% ÷ 80,95%.
const impostoNfAccess = (base) => (base > 0 ? centavos((base * 0.1905) / (1 - 0.1905)) : 0);

// Linha na base do Imposto NF Access do grupo (mesmas condições do recálculo da tela e de
// OrcamentoChiesiImpostoPolicy::calcularValorVigente): marcada, não é a 1945, sem % Imposto NF
// próprio e não paga no exterior.
export function linhaNaBaseImpostoNfAccessGrupo(linha = {}, fornecedorAccess = null) {
    return String(linha?.produto?.codigo ?? '').trim() !== '1945'
        && flagAtiva(linha?.inserirCalculoTotalPlanilha)
        && !ehSomenteVisualizacao(linha)
        && !ImpostoNfAccessPorFornecedorPolicy.temImpostoNfProprio(linha, fornecedorAccess)
        // Coordenação Chiesi no exterior (04/10): o principal entra na NF do bloco, como na planilha.
        && (!!linha?.nfAccessPrincipalExteriorChiesi || !linhaElegivelRemessaExterior(linha, fornecedorAccess));
}

// Opções do Imposto NF Access derivadas do evento do orçamento (como a API): Chiesi ou internacional.
export function opcoesImpostoNfAccessOrcamento(orcamento = {}, opcoes = {}) {
    const evento = orcamento.evento || {};
    const clienteEhChiesi = opcoes.clienteEhChiesi ?? semAcento(evento?.cliente?.nome).includes('CHIESI');
    const aplicaImposto = opcoes.aplicaImposto ?? (clienteEhChiesi || semAcento(evento?.nacionalidade) === 'INTERNACIONAL');
    return { aplicaImposto, clienteEhChiesi };
}

// Imposto NF Access (19,05% por fora) de UM grupo sobre `valorLinha(linha, k)` (k = índice no grupo).
// Fórmula ÚNICA do front (valorTotal, editor, Total Access, Resumo), espelho de
// OrcamentoChiesiImpostoPolicy::calcularValorVigente: 0 sem imposto no evento ou no grupo AGÊNCIA com
// 1945 manual (a linha manual já está no grupo — sem contagem dupla). `incluirLinha` restringe a base
// (ex.: Total Access tira o que o Cliente paga).
function baseImpostoNfAccessGrupo(grupo = {}, valorLinha, { aplicaImposto = false, clienteEhChiesi = false, fornecedorAccess = null, incluirLinha = null } = {}) {
    if (!aplicaImposto || ChiesiAgenciaImpostoPolicy.resolver(grupo, clienteEhChiesi)) return 0;
    return (grupo.subitem || []).reduce((soma, linha, k) => (
        // D-0112: linha na conta direta entra com a base equivalente (impostoNfContaDiretaPolicy.fatorBaseNf).
        linhaNaBaseImpostoNfAccessGrupo(linha, fornecedorAccess) && (!incluirLinha || incluirLinha(linha)) ? soma + valorLinha(linha, k) * fatorBaseNf(linha) : soma
    ), 0);
}
export function impostoNfAccessGrupo(grupo = {}, valorLinha, opcoes = {}) {
    return impostoNfAccess(baseImpostoNfAccessGrupo(grupo, valorLinha, opcoes));
}

// D-0153 (Chiesi nacional, Rafael 03/11: "o Fee é calculado com o imposto"): Imposto NF Access de UMA linha sobre
// `total` (total da linha na etapa, moeda dela), sem arredondar — a parte dela na NF virtual do grupo (mesmo
// filtro e conta de baseImpostoNfAccessGrupo: a soma das linhas é a NF do grupo antes do centavo) ou o "% Imposto
// NF" próprio, que já está no total. 0 quando a linha não gera NF Access (1945, grupo AGÊNCIA com 1945 manual).
// Espelho de OrcamentoImpostoNfAccess::nfAccessDaLinha (API).
export function impostoNfAccessDaLinha(grupo = {}, linha = {}, total = 0, { aplicaImposto = false, clienteEhChiesi = false, fornecedorAccess = null } = {}) {
    const valor = numero(total);
    if (!valor || ehSomenteVisualizacao(linha) || String(linha?.produto?.codigo ?? '').trim() === '1945') return 0;
    if (ImpostoNfAccessPorFornecedorPolicy.temImpostoNfProprio(linha, fornecedorAccess)) {
        const p = numero(linha.imposto) / 100;
        return ehImpostoNfContaDireta(linha) ? (valor * p) / (1 + p) : valor * p;
    }
    if (!aplicaImposto || ChiesiAgenciaImpostoPolicy.resolver(grupo, clienteEhChiesi)) return 0;
    if (!linhaNaBaseImpostoNfAccessGrupo(linha, fornecedorAccess)) return 0;
    return (valor * fatorBaseNf(linha) * 0.1905) / (1 - 0.1905);
}

// NF Access do grupo por ETAPA (colunas da tela), com a linha na moeda do orçamento pelo mesmo fator
// do valorTotal (valorLinhaNaEtapa: câmbio da linha ÷ câmbio global; legado × 1). Linha exterior fica
// fora da base, então o estado é sempre o normal (coluna gravada).
const ESTADO_NORMAL = { estado: 'normal', principal_brl: 0 };
export function impostoNfAccessGrupoPorEtapa(grupo = {}, { cambio = 1, ...opcoes } = {}) {
    const fatorCambio = Number.isFinite(cambio) && cambio > 0 ? cambio : 1;
    const naEtapa = (etapa) => impostoNfAccessGrupo(grupo, (linha) => valorLinhaNaEtapa(linha, etapa, ESTADO_NORMAL, fatorCambio, false), opcoes);
    return { preEvento: naEtapa('PreEvento'), negociado: naEtapa('Negociado'), fechamento: naEtapa('Fechamento') };
}

// Σ NF Access dos grupos por etapa (moeda do orçamento). opcoes: { aplicaImposto, clienteEhChiesi,
// incluirLinha } — sem aplica/Chiesi, derivados do evento do orçamento.
export function calcularImpostoNfAccessPorEtapa(orcamento = {}, fornecedorAccess = null, opcoes = {}) {
    const contexto = { ...opcoes, ...opcoesImpostoNfAccessOrcamento(orcamento, opcoes), fornecedorAccess, cambio: cambioOrcamento(orcamento) };
    return (orcamento.orcamentoitem || []).reduce((totais, grupo) => {
        const nf = impostoNfAccessGrupoPorEtapa(grupo, contexto);
        return { preEvento: centavos(totais.preEvento + nf.preEvento), negociado: centavos(totais.negociado + nf.negociado), fechamento: centavos(totais.fechamento + nf.fechamento) };
    }, { preEvento: 0, negociado: 0, fechamento: 0 });
}

// ---------------------------------------------------------------------------------------------
// Valor exato em reais (contrato 02/10, regras 2–3; valorBrlPolicy). Toda exibição em R$ é a soma dos
// R$ das linhas (cada uma arredondada) + R$ das automáticas; nunca subtotal na moeda × câmbio.
// `exato` (orcamentoBrlExato): a NF Access é calculada sobre a base em R$ e o FEE usa o total sem
// arredondar; fora dele (legado), a NF em R$ = NF gravada na moeda × câmbio (só exibição).
// ---------------------------------------------------------------------------------------------

// R$ da linha na etapa, com o estado da coluna vigente (ledger concluído = principal pago em R$;
// exterior aberto no Fechamento = estimativa Negociado/Pré).
export function valorBrlLinhaNaEtapa(linha = {}, etapa, estado = { estado: 'normal' }, cambio = 1, exato = false) {
    if (etapa === 'Fechamento' && estado.estado === 'ledger') return numero(estado.principal_brl);
    if (etapa === 'Fechamento' && estado.estado === 'aberta') {
        const estimativa = etapaEstimativaAtual(linha);
        if (!estimativa) return 0;
        return valorBrlLinha(linha, estimativa === 'neg' ? 'Negociado' : 'PreEvento', cambio, { exato });
    }
    return valorBrlLinha(linha, etapa, cambio, { exato });
}

// Imposto NF Access de UM grupo em R$. valorBrl/valorMoeda: (linha, k) → valor da linha.
// Contrato v2 B: no orçamento estrangeiro (exato) a NF é 19,05% por fora sobre a base em R$, sem arredondar.
export function impostoNfAccessGrupoBrl(grupo = {}, valorBrl, valorMoeda, { cambio = 1, exato = false, ...opcoes } = {}) {
    if (exato) {
        const base = baseImpostoNfAccessGrupo(grupo, valorBrl, opcoes);
        return base > 0 ? (base * 0.1905) / (1 - 0.1905) : 0;
    }
    return impostoNfAccessGrupo(grupo, valorMoeda, opcoes) * cambio;
}

// R$ de UM grupo por etapa: { preEvento, negociado, fechamento, nf: {…}, linhas: {…} }.
// opcoes: { cambio, exato, aplicaImposto, clienteEhChiesi, fornecedorAccess, incluirLinha, fechamentoExibicao }.
// fechamentoExibicao: no Fechamento, a linha com exibição do fechamento exterior entra com o R$ pago/recalculado.
const CHAVE_ETAPA = { PreEvento: 'preEvento', Negociado: 'negociado', Fechamento: 'fechamento' };
const informado = (valor) => valor !== undefined && valor !== null && valor !== '';
const brlExibicaoFechamento = (linha = {}, cambio = 1) => {
    const exibicao = linha.fechamento_exibicao;
    if (!exibicao) return null;
    if (exibicao.origem === 'ledger') {
        return informado(exibicao.principal_brl) ? numero(exibicao.principal_brl)
            : numero(exibicao.totalFechamento) * cambioBrlLinha(linha, 'Fechamento', cambio);
    }
    return informado(exibicao.total_brl) ? numero(exibicao.total_brl) : numero(exibicao.totalFechamento) * cambio;
};
export function valoresBrlGrupoPorEtapa(grupo = {}, { cambio = 1, exato = false, incluirLinha = null, fechamentoExibicao = false, ...opcoesNf } = {}) {
    const fatorCambio = Number.isFinite(cambio) && cambio > 0 ? cambio : 1;
    const resultado = { preEvento: 0, negociado: 0, fechamento: 0, nf: { preEvento: 0, negociado: 0, fechamento: 0 }, linhas: { preEvento: 0, negociado: 0, fechamento: 0 } };
    ETAPAS_VIGENTE.forEach((etapa) => {
        const chave = CHAVE_ETAPA[etapa];
        const brl = (linha) => {
            const exibido = fechamentoExibicao && etapa === 'Fechamento' ? brlExibicaoFechamento(linha, fatorCambio) : null;
            return exibido !== null ? exibido : valorBrlLinha(linha, etapa, fatorCambio, { exato });
        };
        // Opção (D-0113): mostra o valor na linha, mas não soma no grupo.
        const linhas = (grupo.subitem || []).reduce((soma, linha) => (!linha || ehSomenteVisualizacao(linha) || (incluirLinha && !incluirLinha(linha)) ? soma : soma + brl(linha)), 0);
        const nf = impostoNfAccessGrupoBrl(grupo, (linha) => valorBrlLinha(linha, etapa, fatorCambio, { exato }),
            (linha) => valorLinhaNaEtapa(linha, etapa, ESTADO_NORMAL, fatorCambio, false), { ...opcoesNf, incluirLinha, cambio: fatorCambio, exato });
        resultado.linhas[chave] = linhas;
        resultado.nf[chave] = nf;
        resultado[chave] = linhas + nf;
    });
    return resultado;
}

// Taxas do exterior de UM grupo em R$ por etapa (itens 2 e 4, pós-QA 02/10): Pré/Negociado = encargos
// estimados da remessa (imposto de remessa + envios + NF Access) das linhas Exterior; Fechamento =
// encargos REAIS das linhas com fechamento exterior concluído (os estimados não valem no Fechamento).
// `contexto` = orçamento (moeda/câmbio); `incluirLinha` restringe (TOTAL ACCESS tira o que o Cliente paga).
export function encargosExteriorGrupoBrl(grupo = {}, contexto = {}, { fornecedorAccess = null, incluirLinha = null } = {}) {
    const total = { preEvento: 0, negociado: 0, fechamento: 0 };
    (grupo?.subitem || []).forEach((linha) => {
        if (!linha || ehSomenteVisualizacao(linha) || (incluirLinha && !incluirLinha(linha))) return;
        const calculado = calcularRemessaExteriorEstimativa(linha, contexto, fornecedorAccess, { exato: true });
        if (calculado.elegivel) {
            total.preEvento += calculado.pre.adicional_brl;
            total.negociado += calculado.neg.adicional_brl;
        }
        const real = linha.fechamento_exibicao?.origem === 'ledger' ? linha.fechamento_exibicao.encargos_brl : null;
        if (real) total.fechamento += Object.values(real).reduce((soma, valor) => soma + numero(valor), 0);
    });
    return total;
}

// D-0156 (Rafael 04/11): totalizador do bloco "igual à planilha" (Excel Sanofi Inter: IOF/IRR, TAXA DE
// REMESSA e IMPOSTO NF do bloco, em R$). Mesmas fontes de encargosExteriorGrupoBrl, separadas por componente:
// Pré/Negociado = estimativa da remessa (exata) das linhas Exterior; Fechamento = encargos REAIS do ledger
// concluído (sem ledger = 0, como no Excel). Σ dos 3 componentes = encargosExteriorGrupoBrl. Só exibição.
export const COMPONENTES_ENCARGO_EXTERIOR = ['imposto_remessa_brl', 'taxa_envio_brl', 'imposto_nf_brl'];
export function encargosExteriorGrupoPorComponenteBrl(grupo = {}, contexto = {}, { fornecedorAccess = null, incluirLinha = null } = {}) {
    const resultado = { elegivel: false };
    COMPONENTES_ENCARGO_EXTERIOR.forEach((campo) => { resultado[campo] = { preEvento: 0, negociado: 0, fechamento: 0 }; });
    (grupo?.subitem || []).forEach((linha) => {
        if (!linha || ehSomenteVisualizacao(linha) || (incluirLinha && !incluirLinha(linha))) return;
        const calculado = calcularRemessaExteriorEstimativa(linha, contexto, fornecedorAccess, { exato: true });
        const real = linha.fechamento_exibicao?.origem === 'ledger' ? linha.fechamento_exibicao.encargos_brl : null;
        if (calculado.elegivel) resultado.elegivel = true;
        COMPONENTES_ENCARGO_EXTERIOR.forEach((campo) => {
            if (calculado.elegivel) {
                resultado[campo].preEvento += numero(calculado.pre[campo]);
                resultado[campo].negociado += numero(calculado.neg[campo]);
            }
            if (real) resultado[campo].fechamento += numero(real[campo]);
        });
    });
    return resultado;
}

// R$ do orçamento: por grupo e total (Σ grupos), por etapa, sem e com as taxas do exterior.
// opcoes como calcularImpostoNfAccessPorEtapa (+ fechamentoExibicao). A conta em R$ é a exata em todo
// orçamento estrangeiro (item 3: legado também, só exibição); `exato` devolvido = modo de GRAVAÇÃO
// (orcamentoBrlExato), o único em que o valor na moeda passa a ser R$ ÷ câmbio.
const somarEtapas = (a, b) => ({
    preEvento: a.preEvento + b.preEvento,
    negociado: a.negociado + b.negociado,
    fechamento: a.fechamento + b.fechamento,
});
const ZERO_ETAPAS = { preEvento: 0, negociado: 0, fechamento: 0 };
export function calcularTotaisBrlOrcamento(orcamento = {}, fornecedorAccess = null, opcoes = {}) {
    const cambio = cambioOrcamento(orcamento);
    const exatoConta = opcoes.exato ?? orcamentoEstrangeiro(orcamento);
    const contexto = { ...opcoes, ...opcoesImpostoNfAccessOrcamento(orcamento, opcoes), fornecedorAccess, cambio, exato: exatoConta };
    const grupos = (orcamento.orcamentoitem || []).map((grupo) => {
        const valores = valoresBrlGrupoPorEtapa(grupo || {}, contexto);
        const encargos = encargosExteriorGrupoBrl(grupo || {}, orcamento, { fornecedorAccess, incluirLinha: opcoes.incluirLinha ?? null });
        const encargosComponentes = encargosExteriorGrupoPorComponenteBrl(grupo || {}, orcamento, { fornecedorAccess, incluirLinha: opcoes.incluirLinha ?? null });
        return { ...valores, encargos, encargosComponentes, comEncargos: somarEtapas(valores, encargos) };
    });
    const total = grupos.reduce((soma, g) => somarEtapas(soma, g), ZERO_ETAPAS);
    const totalComEncargos = grupos.reduce((soma, g) => somarEtapas(soma, g.comEncargos), ZERO_ETAPAS);
    return { exato: orcamentoBrlExato(orcamento), estrangeiro: orcamentoEstrangeiro(orcamento), cambio, grupos, total, totalComEncargos };
}

// valorTotal do orçamento (moeda do orçamento), igual ao da API: Σ linhas na coluna vigente do
// FORNECEDOR de cada linha + Imposto NF Access de cada grupo sobre esses mesmos valores (Chiesi ou
// internacional; grupo AGÊNCIA com 1945 manual fica fora, como na API).
// opcoes: { aplicaImposto, clienteEhChiesi } — sem elas, derivados do evento do orçamento (como a API).
// totalBrl (regra 3): Σ R$ das linhas na mesma coluna + NF Access em R$. Modo R$ exato: total = totalBrl
// ÷ câmbio, arredondado uma vez; legado/Real: total como sempre (nada gravado muda).
export function calcularValorVigenteOrcamento(orcamento = {}, fornecedorAccess = null, opcoes = {}) {
    const grupos = orcamento.orcamentoitem || [];
    const linhas = grupos.flatMap((grupo) => grupo.subitem || []);
    const cambio = cambioOrcamento(orcamento);
    const exato = orcamentoBrlExato(orcamento);
    // Conta em R$ exata em todo orçamento estrangeiro (item 3); o valor na moeda só muda no modo exato.
    const exatoConta = orcamentoEstrangeiro(orcamento);
    const { etapa, total, valores, etapas } = resolverColunaVigentePorFornecedor(linhas, { cambio, fornecedorAccess });
    // Opção (D-0113): fora do total em R$, como já está fora do total na moeda.
    const valoresBrl = linhas.map((linha, i) => (ehSomenteVisualizacao(linha) ? 0
        : valorBrlLinhaNaEtapa(linha, etapas[i], estadoLinhaColunaVigente(linha, fornecedorAccess), cambio, exatoConta)));
    const contexto = { ...opcoesImpostoNfAccessOrcamento(orcamento, opcoes), fornecedorAccess };
    let imposto = 0;
    let impostoBrl = 0;
    let indice = 0;
    grupos.forEach((grupo) => {
        const inicio = indice;
        indice += (grupo.subitem || []).length;
        imposto += impostoNfAccessGrupo(grupo, (linha, k) => valores[inicio + k], contexto);
        impostoBrl += impostoNfAccessGrupoBrl(grupo, (linha, k) => valoresBrl[inicio + k], (linha, k) => valores[inicio + k], { ...contexto, cambio, exato: exatoConta });
    });
    // Contrato v2 B: soma com precisão total; só o valor gravado na moeda (modo exato) é arredondado.
    const totalBrl = valoresBrl.reduce((soma, valor) => soma + valor, 0) + impostoBrl;
    // Long Ping em Real (D-0119): total = Σ linhas sem arredondar (valorBrlLinha), arredondado uma vez, como a planilha.
    const somaSemArredondar = linhas.some((linha) => linha?.[CAMPO_SOMA_SEM_ARREDONDAR]);
    const totalMoeda = exato ? moedaOrcamentoDeBrl(totalBrl, cambio) : (somaSemArredondar ? centavos(totalBrl) : centavos(total + imposto));
    // `etapas`: coluna do fornecedor de cada linha, na ordem de grupos→subitens.
    return { etapa, rotulo: ROTULO_ETAPA_VIGENTE[etapa], total: totalMoeda, totalBrl, exato, etapas };
}
